import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useUserRole } from "../UserRoleContext";
import { supabase } from "../supabaseClient";
import { useOrganization } from "../OrganizationContext";
import { useUser } from "../UserContext";

export default function ProductEventOwnerAdmin() {
  const role = useUserRole();
  const { selectedOrgId } = useOrganization();
  const user = useUser();
  const [rows, setRows] = useState([]);
  const [unlinkedOwners, setUnlinkedOwners] = useState([]);
  const [productLookup, setProductLookup] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [newProduct, setNewProduct] = useState("");
  const [newEventOwner, setNewEventOwner] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const [formError, setFormError] = useState(null);
  const [newMappingOwner, setNewMappingOwner] = useState("");
  const [newMappingProductId, setNewMappingProductId] = useState("");
  const [isSavingMapping, setIsSavingMapping] = useState(false);
  const [mappingMessage, setMappingMessage] = useState(null);
  const [mappingError, setMappingError] = useState(null);
  const [hostnameOwnerFlags, setHostnameOwnerFlags] = useState([]);
  const [newFlagHostname, setNewFlagHostname] = useState("");
  const [newFlagEventOwner, setNewFlagEventOwner] = useState("");
  const [isSavingFlag, setIsSavingFlag] = useState(false);
  const [flagMessage, setFlagMessage] = useState(null);
  const [flagError, setFlagError] = useState(null);
  const [searchParams] = useSearchParams();

  const eventOwnerFromQuery = useMemo(() => {
    const owner = searchParams.get("event_owner");
    return owner ? owner.trim() : "";
  }, [searchParams]);

  const sortedProductLookup = useMemo(() => {
    return [...productLookup].sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );
  }, [productLookup]);

  const fetchRows = async () => {
    const [
      { data: products, error: productsError },
      { data: auditResults, error: auditError },
      { data: mappingRows, error: mappingError },
      { data: lookupRows, error: lookupError },
      { data: flagRows, error: flagError },
    ] =
      await Promise.all([
        supabase
          .from("products")
          .select("id, product, event_owner")
          .order("product", { ascending: true }),
        supabase.from("ga4_event_audit_results").select("event_owner"),
        supabase
          .from("event_owner_product_mappings")
          .select("id, event_owner, product_lookup_id, product_lookup(id, name)")
          .order("event_owner", { ascending: true }),
        supabase
          .from("product_lookup")
          .select("id, name")
          .order("name", { ascending: true }),
        supabase
          .from("ga4_hostname_owner_flags")
          .select("id, hostname, event_owner, created_at")
          .order("hostname", { ascending: true })
          .order("event_owner", { ascending: true }),
      ]);

    if (productsError) {
      console.error("Failed to fetch products", productsError);
    }

    if (auditError) {
      console.error("Failed to fetch GA4 event audit results", auditError);
    }

    if (mappingError) {
      console.error("Failed to fetch event owner mappings", mappingError);
    }

    if (lookupError) {
      console.error("Failed to fetch product lookup", lookupError);
    }

    if (flagError) {
      console.error("Failed to fetch hostname owner flags", flagError);
    }

    setRows(products || []);
    setProductLookup(lookupRows || []);

    const formattedMappings = (mappingRows || []).map((row) => ({
      id: row.id,
      event_owner: row.event_owner || "",
      product_lookup_id:
        row.product_lookup_id || row.product_lookup?.id || "",
      product_name: row.product_lookup?.name || "",
    }));
    setMappings(formattedMappings);

    setHostnameOwnerFlags(
      (flagRows || []).map((row) => ({
        id: row.id,
        hostname: row.hostname || "",
        event_owner: row.event_owner || "",
        created_at: row.created_at || null,
      })),
    );

    if (auditResults) {
      const assignedOwners = new Set(
        (products || [])
          .map((row) => row.event_owner?.trim())
          .filter((owner) => owner)
      );

      for (const mapping of formattedMappings) {
        if (mapping.event_owner) {
          assignedOwners.add(mapping.event_owner.trim());
        }
      }

      const missingOwners = auditResults
        .map((result) => result.event_owner?.trim())
        .filter((owner) => owner && !assignedOwners.has(owner));

      setUnlinkedOwners([...new Set(missingOwners)].sort());
    } else {
      setUnlinkedOwners([]);
    }
  };

  useEffect(() => {
    if (role === "admin") fetchRows();
  }, [role]);

  useEffect(() => {
    if (eventOwnerFromQuery) {
      setNewEventOwner(eventOwnerFromQuery);
      setNewMappingOwner(eventOwnerFromQuery);
    }
  }, [eventOwnerFromQuery]);

  const updateRow = async (row) => {
    const { error } = await supabase
      .from("products")
      .update({ event_owner: row.event_owner })
      .eq("id", row.id);
    if (!error) fetchRows();
  };

  const addProduct = async () => {
    setFormError(null);
    setFormMessage(null);

    const product = newProduct.trim();
    const event_owner = newEventOwner.trim();

    if (!selectedOrgId) {
      setFormError("An organization must be selected before adding a product.");
      return;
    }

    if (!product) {
      setFormError("Product name is required.");
      return;
    }

    setIsAdding(true);
    const { error } = await supabase.from("products").insert({
      product,
      event_owner: event_owner || null,
      organization_id: selectedOrgId,
    });

    if (error) {
      console.error("Failed to add product", error);
      setFormError("Failed to add product. Please try again.");
    } else {
      setFormMessage("Product added successfully.");
      setNewProduct("");
      if (!eventOwnerFromQuery) {
        setNewEventOwner("");
      }
      fetchRows();
    }

    setIsAdding(false);
  };

  const addMapping = async () => {
    setMappingError(null);
    setMappingMessage(null);

    const event_owner = newMappingOwner.trim();
    const product_lookup_id = newMappingProductId;

    if (!event_owner) {
      setMappingError("Event owner is required.");
      return;
    }

    if (!product_lookup_id) {
      setMappingError("A product selection is required.");
      return;
    }

    setIsSavingMapping(true);
    const { error } = await supabase
      .from("event_owner_product_mappings")
      .insert({ event_owner, product_lookup_id });

    if (error) {
      console.error("Failed to add event owner mapping", error);
      setMappingError("Failed to add mapping. Please try again.");
    } else {
      setMappingMessage("Mapping added successfully.");
      setNewMappingOwner("");
      setNewMappingProductId("");
      if (!eventOwnerFromQuery) {
        setNewEventOwner("");
      }
      fetchRows();
    }

    setIsSavingMapping(false);
  };

  const updateMappingRow = async (row) => {
    setMappingError(null);
    setMappingMessage(null);

    if (!row.event_owner?.trim() || !row.product_lookup_id) {
      setMappingError("Event owner and product are required to save a mapping.");
      return;
    }

    const { error } = await supabase
      .from("event_owner_product_mappings")
      .update({
        event_owner: row.event_owner.trim(),
        product_lookup_id: row.product_lookup_id,
      })
      .eq("id", row.id);

    if (error) {
      console.error("Failed to update mapping", error);
      setMappingError("Failed to update mapping. Please try again.");
    } else {
      fetchRows();
    }
  };

  const deleteMappingRow = async (rowId) => {
    setMappingError(null);
    setMappingMessage(null);

    const { error } = await supabase
      .from("event_owner_product_mappings")
      .delete()
      .eq("id", rowId);

    if (error) {
      console.error("Failed to delete mapping", error);
      setMappingError("Failed to delete mapping. Please try again.");
    } else {
      fetchRows();
    }
  };

  const addHostnameOwnerFlag = async () => {
    setFlagError(null);
    setFlagMessage(null);

    const hostname = newFlagHostname.trim();
    const event_owner = newFlagEventOwner.trim();

    if (!hostname) {
      setFlagError("Hostname is required.");
      return;
    }

    if (!event_owner) {
      setFlagError("Event owner is required.");
      return;
    }

    setIsSavingFlag(true);
    const { error } = await supabase.from("ga4_hostname_owner_flags").insert({
      hostname,
      event_owner,
      created_by: user?.id || null,
    });

    if (error) {
      console.error("Failed to add hostname owner flag", error);
      setFlagError("Failed to save flag. Please try again.");
    } else {
      setFlagMessage("Flag saved successfully.");
      setNewFlagHostname("");
      setNewFlagEventOwner("");
      fetchRows();
    }

    setIsSavingFlag(false);
  };

  const deleteHostnameOwnerFlag = async (id) => {
    if (!id) return;
    setFlagError(null);
    setFlagMessage(null);

    const { error } = await supabase
      .from("ga4_hostname_owner_flags")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Failed to delete hostname owner flag", error);
      setFlagError("Failed to delete flag. Please try again.");
    } else {
      setFlagMessage("Flag removed.");
      fetchRows();
    }
  };

  if (role !== "admin") {
    return <div className="page-container">Access denied.</div>;
  }

  return (
    <div className="page-container">
      <h2>Product Event Owners</h2>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginBottom: "2rem",
          maxWidth: "640px",
        }}
      >
        <h3 style={{ marginBottom: "0.25rem" }}>Global Event Owner Mapping</h3>
        <p style={{ color: "#555", marginTop: 0 }}>
          Map GA4 <code>event_owner</code> values to canonical products so that
          matching products can be synchronized across every account.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.75rem",
            alignItems: "end",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span>Event Owner</span>
            <input
              type="text"
              value={newMappingOwner}
              onChange={(e) => setNewMappingOwner(e.target.value)}
              list="event-owner-suggestions"
              placeholder="Enter event owner"
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span>Product</span>
            <select
              value={newMappingProductId}
              onChange={(e) => setNewMappingProductId(e.target.value)}
            >
              <option value="">Select product</option>
              {sortedProductLookup.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button onClick={addMapping} disabled={isSavingMapping}>
              {isSavingMapping ? "Saving..." : "Save Mapping"}
            </button>
            {mappingMessage && <span style={{ color: "#0a7e0a" }}>{mappingMessage}</span>}
            {mappingError && <span style={{ color: "#c00" }}>{mappingError}</span>}
          </div>
        </div>
        <datalist id="event-owner-suggestions">
          {unlinkedOwners.map((owner) => (
            <option key={owner} value={owner} />
          ))}
        </datalist>
        <table className="ui-table" style={{ width: "100%", borderCollapse: "collapse", marginTop: "1rem" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc" }}>Event Owner</th>
              <th style={{ border: "1px solid #ccc" }}>Product</th>
              <th style={{ border: "1px solid #ccc" }}></th>
            </tr>
          </thead>
          <tbody>
            {mappings.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ border: "1px solid #ccc", textAlign: "center", padding: "0.75rem" }}>
                  No mappings have been configured yet.
                </td>
              </tr>
            ) : (
              mappings.map((row) => (
                <tr key={row.id}>
                  <td style={{ border: "1px solid #ccc" }}>
                    <input
                      type="text"
                      value={row.event_owner}
                      onChange={(e) =>
                        setMappings((prev) =>
                          prev.map((m) =>
                            m.id === row.id
                              ? { ...m, event_owner: e.target.value }
                              : m,
                          ),
                        )
                      }
                    />
                  </td>
                  <td style={{ border: "1px solid #ccc" }}>
                    <select
                      value={row.product_lookup_id || ""}
                      onChange={(e) =>
                        setMappings((prev) =>
                          prev.map((m) =>
                            m.id === row.id
                              ? {
                                  ...m,
                                  product_lookup_id: e.target.value,
                                  product_name:
                                    sortedProductLookup.find(
                                      (option) => option.id === e.target.value,
                                    )?.name || "",
                                }
                              : m,
                          ),
                        )
                      }
                    >
                      <option value="">Select product</option>
                      {sortedProductLookup.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td style={{ border: "1px solid #ccc" }}>
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
                      <button onClick={() => updateMappingRow(row)}>Save</button>
                      <button onClick={() => deleteMappingRow(row.id)} style={{ color: "#a00" }}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginBottom: "2rem",
          maxWidth: "640px",
        }}
      >
        <h3 style={{ marginBottom: "0.25rem" }}>Hostname Owner Flags</h3>
        <p style={{ color: "#555", marginTop: 0 }}>
          Flag hostname and event owner combinations that should be ignored when
          checking cross-domain coverage.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.75rem",
            alignItems: "end",
          }}
        >
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span>Hostname</span>
            <input
              type="text"
              value={newFlagHostname}
              onChange={(e) => setNewFlagHostname(e.target.value)}
              placeholder="example.com"
            />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
            <span>Event Owner</span>
            <input
              type="text"
              value={newFlagEventOwner}
              onChange={(e) => setNewFlagEventOwner(e.target.value)}
              placeholder="Enter event owner"
            />
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            <button onClick={addHostnameOwnerFlag} disabled={isSavingFlag}>
              {isSavingFlag ? "Saving..." : "Add Flag"}
            </button>
            {flagMessage && <span style={{ color: "#0a7e0a" }}>{flagMessage}</span>}
            {flagError && <span style={{ color: "#c00" }}>{flagError}</span>}
          </div>
        </div>
        <table className="ui-table" style={{ width: "100%", borderCollapse: "collapse", marginTop: "1rem" }}>
          <thead>
            <tr>
              <th style={{ border: "1px solid #ccc" }}>Hostname</th>
              <th style={{ border: "1px solid #ccc" }}>Event Owner</th>
              <th style={{ border: "1px solid #ccc" }}></th>
            </tr>
          </thead>
          <tbody>
            {hostnameOwnerFlags.length === 0 ? (
              <tr>
                <td
                  colSpan={3}
                  style={{
                    border: "1px solid #ccc",
                    textAlign: "center",
                    padding: "0.75rem",
                  }}
                >
                  No hostname owner flags have been added.
                </td>
              </tr>
            ) : (
              hostnameOwnerFlags.map((flag) => (
                <tr key={flag.id}>
                  <td style={{ border: "1px solid #ccc" }}>{flag.hostname}</td>
                  <td style={{ border: "1px solid #ccc" }}>{flag.event_owner || "—"}</td>
                  <td style={{ border: "1px solid #ccc", textAlign: "center" }}>
                    <button
                      onClick={() => deleteHostnameOwnerFlag(flag.id)}
                      style={{ color: "#a00" }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5rem",
          marginBottom: "1.5rem",
          maxWidth: "480px",
        }}
      >
        <h3 style={{ marginBottom: "0.25rem" }}>Add Product</h3>
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span>Product Name</span>
          <input
            type="text"
            value={newProduct}
            onChange={(e) => setNewProduct(e.target.value)}
            placeholder="Enter product name"
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span>Event Owner</span>
          <input
            type="text"
            value={newEventOwner}
            onChange={(e) => setNewEventOwner(e.target.value)}
            placeholder="Enter event owner (optional)"
          />
        </label>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <button onClick={addProduct} disabled={isAdding}>
            {isAdding ? "Adding..." : "Add Product"}
          </button>
          {formMessage && <span style={{ color: "#0a7e0a" }}>{formMessage}</span>}
          {formError && <span style={{ color: "#c00" }}>{formError}</span>}
        </div>
        {eventOwnerFromQuery && (
          <small style={{ color: "#555" }}>
            Prefilled from URL parameter: <strong>{eventOwnerFromQuery}</strong>
          </small>
        )}
      </div>
      <table className="ui-table" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ border: "1px solid #ccc" }}>Product</th>
            <th style={{ border: "1px solid #ccc" }}>Event Owner</th>
            <th style={{ border: "1px solid #ccc" }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td style={{ border: "1px solid #ccc" }}>{row.product}</td>
              <td style={{ border: "1px solid #ccc" }}>
                <input
                  type="text"
                  value={row.event_owner || ""}
                  onChange={(e) =>
                    setRows((prev) =>
                      prev.map((r) =>
                        r.id === row.id ? { ...r, event_owner: e.target.value } : r
                      )
                    )
                  }
                />
              </td>
              <td style={{ border: "1px solid #ccc" }}>
                <button onClick={() => updateRow(row)}>Save</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: "1.5rem" }}>
        <h3>Unlinked Event Owners</h3>
        {unlinkedOwners.length === 0 ? (
          <p>All account event owners are linked to products.</p>
        ) : (
          <ul>
            {unlinkedOwners.map((owner) => (
              <li key={owner}>{owner}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
